import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "./axiosSetup";
import moment from "moment";
import "bootstrap/dist/css/bootstrap.min.css";
import CustomNavbar from "./CustomNavbar";
import "./css/MoveTable.css";
import DataTable from "./Zunftrat/DataTable";
import cfg from "../config";

const MoveTable = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [moves, setMoves] = useState([]);
  const [haeser, setHaeser] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertType, setAlertType] = useState("success");
  const [selectedMove, setSelectedMove] = useState(null);
  const [selectedHaeser, setSelectedHaeser] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [haesToCancel, setHaesToCancel] = useState(null);
  const [cancelError, setCancelError] = useState(null);
  const [detailMove, setDetailMove] = useState(null);
  const [memberBlockedUntill, setMemberBlockedUntill] = useState(null);

  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
      window.history.replaceState({}, document.title);
    }
    fetchMoves();
  }, []);

  const fetchMoves = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const movesRes = await api.get("/api/umzugListe-member", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const movesData = movesRes.data;
      const movesArray = Array.isArray(movesData) ? movesData : (movesData.moves || []);
      const blockedUntill = Array.isArray(movesData) ? null : (movesData.blockedUntill || null);
      setMoves(movesArray);
      setMemberBlockedUntill(blockedUntill);

      const haesRes = await api.get("/api/my-haeser-for-booking", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHaeser(haesRes.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("authToken");
        navigate("/login", {
          state: { message: "Session abgelaufen! Bitte logge dich erneut ein." },
        });
        return;
      }
      setError("Fehler beim Laden der Daten.");
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (msg, type = "success") => {
    setAlertMessage(msg);
    setAlertType(type);
    setTimeout(() => setAlertMessage(null), 4000);
  };

  const openModal = (move, isWaitingList = false) => {
    setSelectedMove({ ...move, isWaitingList });
    const bookedHaeser = move.fields.find((f) => f.key === "myBookings")?.value || [];
    const ownBookedIDs = bookedHaeser.filter(h => !h.bookedByOther).map(h => h.id).filter(Boolean);
    const memberHaeser = haeser.filter((h) => h.hasTreadmills === 1);

    let initialSelected = ownBookedIDs;
    if (!isWaitingList && memberHaeser.length === 1 && !ownBookedIDs.includes(memberHaeser[0].ID)) {
      initialSelected = [memberHaeser[0].ID];
    }
    setSelectedHaeser(initialSelected);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedMove(null);
    setSelectedHaeser([]);
    setShowModal(false);
  };

  const toggleHaesSelection = (id) => {
    setSelectedHaeser(prev =>
      prev.includes(id) ? prev.filter(h => h !== id) : [...prev, id]
    );
  };

  const submitBooking = async () => {
    if (!selectedMove || selectedHaeser.length === 0) {
      showAlert(`Bitte wähle mindestens ein Element aus (${cfg.costumeTerm}).`, "warning");
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      await api.post(
        "/api/book-move",
        { moveID: selectedMove.id, haeserIDs: selectedHaeser, isWaitingList: selectedMove?.isWaitingList ? 1 : 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showAlert("Buchung erfolgreich!");
      closeModal();
      fetchMoves();
    } catch (err) {
      const serverMessage = err.response?.data?.message;
      closeModal();
      showAlert(serverMessage || "Fehler bei der Buchung.", "danger");
    }
  };

  const cancelBooking = async () => {
    if (!selectedMove) return;
    setCancelError(null);
    const isSingleBooking = haesToCancel?.moveID !== null;
    if (isSingleBooking && !haesToCancel?.moveID) return;

    try {
      const token = localStorage.getItem("authToken");
      const response = await api.post(
        "/api/cancel-booking",
        { moveID: haesToCancel?.moveID || null, haesID: haesToCancel.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { results } = response.data.result;

      if (results.every(r => r.status === "success")) {
        showAlert("Alle Buchungen erfolgreich storniert!");
        setHaesToCancel(null);
        fetchMoves();
        closeModal();
      } else if (results.some(r => r.status === "success")) {
        const errorMessages = results
          .filter(r => r.status === "error")
          .map(r => `${cfg.costumeTerm} ${r.bookingID}: ${r.message}`)
          .join("\n");
        setCancelError(errorMessages || "Einige Buchungen konnten nicht storniert werden.");
        showAlert("Einige Buchungen konnten nicht storniert werden.", "warning");
        fetchMoves();
        closeModal();
      } else {
        const errorMessages = results
          .map(r => `${cfg.costumeTerm} ${r.bookingID}: ${r.message}`)
          .join("\n");
        setCancelError(errorMessages || "Buchung konnte nicht storniert werden.");
        showAlert("Fehler beim Stornieren der Buchung.", "danger");
      }
    } catch (err) {
      closeModal();
      const serverMessage = err.response?.data?.message || "Fehler beim Stornieren der Buchung.";
      setCancelError(serverMessage);
      showAlert(serverMessage, "danger");
    }
  };

  const formatValue = (field) => {
    if (!field || field.value === null || field.value === undefined) return "-";
    switch (field.dataType) {
      case "boolean":
        return field.value ? "Ja" : "Nein";
      case "date":
        return moment(field.value).isValid()
          ? moment(field.value).format("DD.MM.YYYY")
          : field.value;
      case "time":
        return moment(field.value, ["HH:mm:ss", "HH:mm"]).isValid()
          ? moment(field.value, ["HH:mm:ss", "HH:mm"]).format("HH:mm") + " Uhr"
          : field.value;
      case "datetime":
        return moment(field.value).isValid()
          ? moment(field.value).format("DD.MM.YYYY HH:mm") + " Uhr"
          : field.value;
      case "array":
        return Array.isArray(field.value)
          ? field.value.map(h => `${h.number} – ${h.name}`).join(", ")
          : "-";
      case "departures":
        return Array.isArray(field.value) && field.value.length > 0
          ? field.value.map(d => `${d.label}: ${d.time} Uhr`).join(", ")
          : "-";
      default:
        if (Array.isArray(field.value)) {
          return field.value.map(v =>
            typeof v === "object" && v !== null
              ? Object.values(v).filter(x => typeof x !== "object").join(" ")
              : String(v ?? "")
          ).join(", ") || "-";
        }
        if (typeof field.value === "object" && field.value !== null) return "-";
        return field.value;
    }
  };

  const earliestDeparture = (move) => {
    const moveDate = move.fields.find(f => f.key === "date")?.value;
    if (!moveDate) return null;
    const departures = move.fields.find(f => f.key === "departures")?.value || [];
    const times = departures
      .map(d => d.time)
      .filter(Boolean)
      .map(t => moment(`${moveDate} ${t}`, "YYYY-MM-DD HH:mm"))
      .filter(m => m.isValid());
    if (!times.length) return null;
    return times.reduce((earliest, t) => t.isBefore(earliest) ? t : earliest);
  };

  // Flatten move.fields[] into flat objects for DataTable
  const flatMoves = useMemo(() =>
    moves.map(move => {
      const obj = { _id: move.id, _move: move };
      (move.fields || []).forEach(f => { obj[f.frontendName] = f; });
      return obj;
    }), [moves]
  );

  // Build columns from first move's fields
  const columns = useMemo(() => {
    if (!moves.length) return [];
    return moves[0].fields.map(f => ({
      key: f.frontendName,
      label: f.frontendName,
      render: (fieldObj) => {
        if (!fieldObj) return <span className="dt-muted">—</span>;

        if (fieldObj.key === "freeSeats") {
          const seats = parseInt(fieldObj.value ?? 0);
          let cls = "text-success fw-bold";
          if (seats <= 5) cls = "text-danger fw-bold";
          else if (seats < 20) cls = "text-warning fw-bold";
          return <span className={cls}>{seats}</span>;
        }

        if (fieldObj.key === "departures") {
          const deps = Array.isArray(fieldObj.value) ? fieldObj.value : [];
          return deps.length > 0
            ? <span>{deps.map(d => `${d.label}: ${d.time} Uhr`).join(", ")}</span>
            : <span className="dt-muted">—</span>;
        }

        if (fieldObj.key === "myBookings") {
          const myBs = fieldObj.value || [];
          if (!myBs.length) return <span className="dt-muted">Keine Buchungen</span>;
          return (
            <ul className="list-unstyled mb-0 small">
              {myBs.map((b, i) => (
                <li key={b.id ?? i}>
                  {b.number} – {b.name}{" "}
                  {b.isWaitingList
                    ? <span className="text-warning">(Warteliste {b.waitingListPosition})</span>
                    : <span className="text-success">(gebucht)</span>
                  }
                </li>
              ))}
            </ul>
          );
        }

        if (fieldObj.dataType === "boolean") {
          return fieldObj.value
            ? <span className="dt-badge dt-badge-yes">Ja</span>
            : <span className="dt-badge dt-badge-no">Nein</span>;
        }

        const formatted = formatValue(fieldObj);
        if (formatted === "-" || formatted === null || formatted === undefined) {
          return <span className="dt-muted">—</span>;
        }
        return <span>{formatted}</span>;
      },
      searchValue: (fieldObj) => {
        if (!fieldObj) return "";
        if (fieldObj.key === "myBookings") {
          return (fieldObj.value || []).map(b => `${b.number} ${b.name}`).join(" ");
        }
        const v = formatValue(fieldObj);
        if (v === null || v === undefined || typeof v === "object") return "";
        return String(v);
      },
    }));
  }, [moves]);

  const hasBookableMove = moves.some(m => m.fields.find(f => f.key === "bookable")?.value);
  const showActionsCol = true; // Always show actions for detail button

  const isBlockedForMove = (move) => {
    if (!memberBlockedUntill) return false;
    const moveDateStr = move.fields.find(f => f.key === "date")?.value;
    if (!moveDateStr) return false;
    return new Date(memberBlockedUntill + "T23:59:59") >= new Date(moveDateStr + "T00:00:00");
  };

  const actions = (row) => {
    const move = row._move;
    const bookable = move.fields.find(f => f.key === "bookable")?.value;
    const freeSeats = parseInt(move.fields.find(f => f.key === "freeSeats")?.value ?? 0);
    const myBookings = move.fields.find(f => f.key === "myBookings")?.value || [];
    const hasMyBookings = myBookings.length > 0;
    const blockedForThis = isBlockedForMove(move);

    return (
      <div className="dt-actions">
        <button className="dt-btn dt-btn-secondary" onClick={() => setDetailMove(move)} title="Details anzeigen">
          ℹ Details
        </button>
        {blockedForThis && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.5)',
            color: '#f87171', borderRadius: 20, padding: '3px 10px',
            fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
          }}>
            🚫 Gesperrt bis {moment(memberBlockedUntill).format("DD.MM.YYYY")}
          </span>
        )}
        {!blockedForThis && haeser.length > 0 && bookable && hasMyBookings && (
          <button className="dt-btn dt-btn-warning" onClick={() => openModal(move, freeSeats <= 0)}>
            Buchung verwalten
          </button>
        )}
        {!blockedForThis && haeser.length > 0 && bookable && !hasMyBookings && freeSeats > 0 && (
          <button className="dt-btn dt-btn-success" onClick={() => openModal(move, false)}>
            Jetzt anmelden
          </button>
        )}
        {!blockedForThis && haeser.length > 0 && bookable && !hasMyBookings && freeSeats <= 0 && (
          <button className="dt-btn dt-btn-secondary" onClick={() => openModal(move, true)}>
            Auf Warteliste
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <CustomNavbar />
      <div className="container-fluid px-3 px-md-4 mt-4">
        <h2 className="mb-3">{cfg.eventTermPlural} Übersicht</h2>
        {message && <div className="alert alert-success mb-3">{message}</div>}
        {alertMessage && (
          <div className={`alert alert-${alertType} mb-3`}>{alertMessage}</div>
        )}
        <DataTable
          data={flatMoves}
          columns={columns}
          loading={loading}
          error={error}
          actions={actions}
          searchPlaceholder={`${cfg.eventTerm} suchen…`}
          emptyText={`Keine ${cfg.eventTermPlural} gefunden.`}
          initialPageSize={25}
          pageSizeOptions={[10, 25, 50]}
          cardHeader={(row) => {
            const move = row._move;
            const city = move.fields.find(f => f.key === 'city')?.value || '–';
            const rawDate = move.fields.find(f => f.key === 'date')?.value;
            const dateStr = rawDate && moment(rawDate).isValid()
              ? moment(rawDate).format('DD.MM.YYYY')
              : rawDate || '';
            const myBookings = move.fields.find(f => f.key === 'myBookings')?.value || [];
            const freeSeats = parseInt(move.fields.find(f => f.key === 'freeSeats')?.value ?? 0);
            const blockedForThis = isBlockedForMove(move);
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 17, fontWeight: 700 }}>{city}</span>
                  {blockedForThis ? (
                    <span style={{
                      fontSize: 11, background: 'rgba(220,38,38,0.25)', border: '1px solid rgba(220,38,38,0.5)',
                      color: '#fca5a5', borderRadius: 20, padding: '2px 10px', fontWeight: 700, whiteSpace: 'nowrap',
                    }}>
                      🚫 Gesperrt
                    </span>
                  ) : myBookings.length > 0 ? (
                    <span style={{ fontSize: 11, background: 'rgba(255,255,255,0.25)', borderRadius: 20, padding: '2px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      ✓ Gebucht
                    </span>
                  ) : null}
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 13, opacity: 0.88 }}>
                  {dateStr && <span>📅 {dateStr}</span>}
                  {blockedForThis ? (
                    <span style={{ color: '#fca5a5', fontWeight: 600 }}>
                      bis {moment(memberBlockedUntill).format("DD.MM.YYYY")}
                    </span>
                  ) : (
                    <span style={{ color: freeSeats <= 5 ? '#fca5a5' : freeSeats < 20 ? '#fde68a' : '#bbf7d0' }}>
                      {freeSeats} freie Plätze
                    </span>
                  )}
                </div>
              </div>
            );
          }}
        />
      </div>

      {/* Buchungs-Modal */}
      {showModal && selectedMove && (
        <>
          <div className="modern-modal-backdrop" onClick={closeModal}></div>
          <div className="modern-modal">
            <div className={`modern-modal-header ${selectedMove?.isWaitingList ? "waiting-list" : ""}`}>
              <h3>Buchung für {formatValue(selectedMove.fields.find(f => f.key === "city"))}</h3>
              <button className="modern-close-btn" onClick={closeModal}>&times;</button>
            </div>

            <div className="modern-modal-body">
              <div className="row mb-3">
                <div className="col-12 col-md-6">
                  <strong>Datum:</strong> {formatValue(selectedMove.fields.find(f => f.key === "date"))}
                </div>
                {(selectedMove.fields.find(f => f.key === "departures")?.value || []).map((dep, i) => (
                  <div className="col-12 col-md-6" key={i}>
                    <strong>Abfahrt {dep.label}:</strong> {dep.time} Uhr
                  </div>
                ))}
                <div className="col-12 col-md-6">
                  <strong>Beginn:</strong> {formatValue(selectedMove.fields.find(f => f.key === "startDateTime"))}
                </div>
                <div className="col-12 col-md-6">
                  <strong>Besen:</strong> {formatValue(selectedMove.fields.find(f => f.key === "broomNeeded"))}
                </div>
                <div className="col-12 col-md-6">
                  <strong>Definition Nachtumzug:</strong> {formatValue(selectedMove.fields.find(f => f.key === "isNightMove"))}
                </div>
              </div>

              <hr />

              {(() => {
                const allMyBookings = selectedMove.fields.find(f => f.key === "myBookings")?.value || [];
                const ownBookings = allMyBookings.filter(b => !b.bookedByOther);
                const otherBookings = allMyBookings.filter(b => b.bookedByOther);
                const dep = earliestDeparture(selectedMove);
                const canCancel = dep ? moment().isBefore(dep.clone().subtract(2, "hours")) : true;

                return (
                  <div className="mb-3">
                    <strong>Deine Buchungen:</strong>
                    {ownBookings.length === 0 ? (
                      <p className="text-muted mb-2">Du hast noch nichts gebucht.</p>
                    ) : (
                      <ul className="list-group mb-3">
                        {ownBookings.map((b, idx) => {
                          const waitingListPos = selectedMove.isWaitingList && b.isWaitingList ? b.waitingListPosition : null;
                          return (
                            <li key={idx} className="list-group-item d-flex justify-content-between align-items-center">
                              <span>
                                {b.number} – {b.name}{" "}
                                {waitingListPos ? `(Warteliste Platz: ${waitingListPos})` : ""}
                              </span>
                              <div>
                                {canCancel ? (
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => {
                                      setSelectedMove(selectedMove);
                                      setHaesToCancel({ ...b, moveID: selectedMove.id });
                                    }}
                                    title="Buchung stornieren"
                                  >
                                    ✖ Stornieren
                                  </button>
                                ) : (
                                  <span className="text-muted small fst-italic">
                                    Keine Stornierung mehr möglich (Abfahrt in &lt; 2 Stunden)
                                  </span>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}

                    {otherBookings.length > 0 && (
                      <>
                        <strong>Bereits gebucht:</strong>
                        <ul className="list-group mt-1">
                          {otherBookings.map((b, idx) => (
                            <li
                              key={idx}
                              className="list-group-item d-flex justify-content-between align-items-center"
                              style={{ backgroundColor: "rgba(255,193,7,0.12)", borderLeft: "3px solid #ffc107" }}
                            >
                              <span>{b.number} – {b.name}</span>
                              <span className="badge bg-warning text-dark">
                                Gebucht von: {b.bookedByName}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                );
              })()}

              <hr />

              <div className="mb-3">
                <strong>Deine {cfg.costumeTermPlural} für die Buchung auswählen:</strong>
                <div className="haeser-grid">
                  {haeser
                    .filter(h =>
                      !selectedMove.fields
                        .find(f => f.key === "myBookings")?.value
                        .some(b => b.id === h.ID)
                    )
                    .map(h => {
                      const isNightMove = selectedMove.fields.find(f => f.key === "isNightMove")?.value;
                      const isKidsHaes = h.isKidsHaes === 1;
                      const isDisabled = isNightMove && isKidsHaes;
                      return (
                        <label
                          key={h.ID}
                          className={`haes-card ${isDisabled ? "disabled-haes" : ""}`}
                          style={{ opacity: isDisabled ? 0.6 : 1, cursor: isDisabled ? "not-allowed" : "pointer" }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedHaeser.includes(h.ID)}
                            onChange={() => toggleHaesSelection(h.ID)}
                            disabled={isDisabled}
                          />
                          <span>{h.number} – {h.name}</span>
                          {isDisabled && (
                            <div className="text-danger small mt-1">
                              Kinder können bei Nachtumzügen leider nicht teilnehmen
                            </div>
                          )}
                        </label>
                      );
                    })}
                </div>
              </div>

              <div className="mb-2 text-dark">
                <strong>Belegte Plätze:</strong> {selectedMove.fields.find(f => f.key === "bookedSeats")?.value || "0/0"} &nbsp;
                (<strong>Frei:</strong> {selectedMove.fields.find(f => f.key === "freeSeats")?.value ?? 0})
              </div>
            </div>

            <div className="modern-modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>Schließen</button>
              {(() => {
                const ownBookedIDs = (selectedMove.fields.find(f => f.key === "myBookings")?.value || [])
                  .filter(b => !b.bookedByOther).map(b => b.id);
                const hasNewSelection = selectedHaeser.some(id => !ownBookedIDs.includes(id));
                if (!hasNewSelection) return null;
                return (
                  <button className="btn btn-success" onClick={submitBooking}>
                    {selectedMove.isWaitingList ? "Auf Warteliste setzen" : "Buchung abschicken"}
                  </button>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {/* Storno-Modal */}
      {haesToCancel && (
        <>
          <div className="modern-modal-backdrop" />
          <div className="modern-modal modern-modal-sm animate-fade-in">
            <div className="modern-modal-header danger">
              <h3>Buchung stornieren</h3>
              <button
                className="modern-close-btn"
                onClick={() => { setHaesToCancel(null); setCancelError(null); }}
              >
                &times;
              </button>
            </div>
            <div className="modern-modal-body text-dark">
              {cancelError ? (
                <div className="alert alert-danger">{cancelError}</div>
              ) : (
                <p>
                  Willst du die Buchung für {cfg.costumeTerm}{" "}
                  <strong>{haesToCancel.number} - {haesToCancel.name}</strong> wirklich stornieren?
                </p>
              )}
            </div>
            <div className="modern-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => { setHaesToCancel(null); setCancelError(null); }}
              >
                Abbrechen
              </button>
              {!cancelError && (
                <button className="btn btn-danger" onClick={cancelBooking}>
                  Buchung stornieren
                </button>
              )}
            </div>
          </div>
        </>
      )}
      {/* Umzugs-Detail-Modal */}
      {detailMove && (
        <>
          <div className="modern-modal-backdrop" onClick={() => setDetailMove(null)}></div>
          <div className="modern-modal">
            <div className="modern-modal-header">
              <h3>Details – {detailMove.fields.find(f => f.key === 'city')?.value || cfg.eventTerm}</h3>
              <button className="modern-close-btn" onClick={() => setDetailMove(null)}>&times;</button>
            </div>
            <div className="modern-modal-body">
              <div className="row g-3">
                {detailMove.fields
                  .filter(f => !['myBookings', 'bookable', 'freeSeats'].includes(f.key))
                  .map(f => (
                    <div className="col-12 col-md-6" key={f.key}>
                      <div className="small text-secondary mb-1">{f.frontendName}</div>
                      <div className="fw-semibold">
                        {f.key === 'departures' && Array.isArray(f.value) && f.value.length > 0
                          ? f.value.map((d, i) => (
                              <div key={i}>{d.label}: {d.time} Uhr</div>
                            ))
                          : formatValue(f) || '–'
                        }
                      </div>
                    </div>
                  ))
                }
              </div>
              {(() => {
                const myBookings = detailMove.fields.find(f => f.key === 'myBookings')?.value || [];
                return myBookings.length > 0 ? (
                  <div className="mt-3">
                    <hr />
                    <strong>Deine Buchungen:</strong>
                    <ul className="list-group mt-2">
                      {myBookings.map((b, i) => (
                        <li key={i} className="list-group-item d-flex justify-content-between">
                          <span>{b.number} – {b.name}</span>
                          <span className={b.isWaitingList ? 'text-warning' : 'text-success'}>
                            {b.isWaitingList ? `Warteliste ${b.waitingListPosition}` : '✓ Gebucht'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null;
              })()}
            </div>
            <div className="modern-modal-footer">
              <button className="btn btn-secondary" onClick={() => setDetailMove(null)}>Schließen</button>
              {(() => {
                const bookable = detailMove.fields.find(f => f.key === 'bookable')?.value;
                const freeSeats = parseInt(detailMove.fields.find(f => f.key === 'freeSeats')?.value ?? 0);
                const myBookings = detailMove.fields.find(f => f.key === 'myBookings')?.value || [];

                if (!bookable) {
                  if (memberBlockedUntill) {
                    const moveDateStr = detailMove.fields.find(f => f.key === 'date')?.value;
                    const blockedUntilDate = new Date(memberBlockedUntill + "T23:59:59");
                    const moveDate = moveDateStr ? new Date(moveDateStr + "T00:00:00") : null;
                    if (moveDate && blockedUntilDate >= moveDate) {
                      const formatted = moment(memberBlockedUntill).format("DD.MM.YYYY");
                      return (
                        <span className="text-warning d-flex align-items-center gap-1 small">
                          ⚠️ Du bist bis {formatted} gesperrt
                        </span>
                      );
                    }
                  }
                  return null;
                }

                if (!haeser.length) return null;
                return (
                  <button
                    className={`btn ${myBookings.length > 0 ? 'btn-warning' : freeSeats > 0 ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => { setDetailMove(null); openModal(detailMove, freeSeats <= 0); }}
                  >
                    {myBookings.length > 0 ? 'Buchung verwalten' : freeSeats > 0 ? 'Jetzt anmelden' : 'Auf Warteliste'}
                  </button>
                );
              })()}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MoveTable;
